import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { Table, Button, Pagination } from '../../components/Common';
import type { TableColumn } from '../../components/Common';
import { applicationsService, VendorApplication, ApplicationsPagination } from '../../services';
import { handleApiError, handleApiSuccess } from '../../services/error.service';
import { useSmartPageSize } from '../../config/pagination';
import { notificationAPI } from '../../hooks/notificationAPI';
import { RequestMoreInfoModal } from '../../components/Applications/RequestMoreInfoModal';

interface ApplicationsNewProps {
  onDataChange?: () => void;
  searchQuery?: string;
  onApplicationsViewed?: (applicationIds: string[]) => void;
  applicationId?: string; // For filtering by specific applicationId from URL
}

const ApplicationsNew: React.FC<ApplicationsNewProps> = ({ onDataChange, searchQuery = '', onApplicationsViewed, applicationId }) => {
  const [applications, setApplications] = useState<VendorApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [requestInfoModalOpen, setRequestInfoModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<VendorApplication | null>(null);
  
  // Smart pagination based on screen size
  const smartPageSize = useSmartPageSize('applications');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(smartPageSize);
  const [pagination, setPagination] = useState<ApplicationsPagination>({
    hasMore: false,
    currentPage: 1,
    totalItems: 0,
    totalPages: 1,
  });

  const fetchPendingApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await applicationsService.getPendingApplications(currentPage, itemsPerPage);
      setApplications(response.applications);
      setPagination(response.pagination);
      console.log('📊 ApplicationsNew: Loaded', response.applications.length, 'applications (notification count managed by sidebar)');
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchPendingApplications();
  }, [currentPage, fetchPendingApplications]);

  const handleApprove = async (vendorId: string) => {
    try {
      setActionLoading(vendorId);
      await applicationsService.approveVendor(vendorId);
      handleApiSuccess('Vendor approved successfully');
      
      // Signal notification resolution to refresh count from API
      await notificationAPI.resolveNotification(vendorId);
      
      // Refresh the list - stay on current page or go to page 1 if current page is empty
      if (applications.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        await fetchPendingApplications();
      }
      // Notify parent to update badge counts
      if (onDataChange) {
        onDataChange();
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRequestMoreInfo = (applicationId: string) => {
    const app = applications.find(a => a.id === applicationId);
    if (app) {
      setSelectedApplication(app);
      setRequestInfoModalOpen(true);
    }
  };

  const handleSubmitInfoRequest = async (message: string) => {
    if (!selectedApplication) return;

    try {
      setActionLoading(selectedApplication.id);
      await applicationsService.requestMoreInfo(selectedApplication.id, message);
      handleApiSuccess('Information request sent successfully');
      
      // Signal notification resolution
      await notificationAPI.resolveNotification(selectedApplication.id);
      
      // Refresh the list
      if (applications.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        await fetchPendingApplications();
      }
      
      if (onDataChange) {
        onDataChange();
      }
    } catch (err) {
      handleApiError(err);
      throw err;
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const time = date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    return { date: `${day} ${month}`, time };
  };

  // Filter applications based on search query (client-side filtering on current page)
  const filteredApplications = applications.filter(app =>
    app.businessName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.contactName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Define table columns
  const columns: TableColumn<VendorApplication>[] = [
    {
      key: 'date',
      label: 'Date',
      width: '90px',
      render: (application) => {
        const { date, time } = formatDate(application.submittedAt || application.createdAt);
        return (
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)', whiteSpace: 'pre-line' }}>
            {`${date},\n${time}`}
          </Typography>
        );
      },
    },
    {
      key: 'business',
      label: 'Business',
      width: 1,
      render: (application) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
            {application.businessName}
          </Typography>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
            {application.category}
          </Typography>
          {application.hasLicense && (
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              Trade license
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'contact',
      label: 'Contact person',
      width: 1,
      render: (application) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
            {application.contactName}
          </Typography>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
            {application.email}
          </Typography>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
            {application.phone}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '250px',
      align: 'right',
      render: (application) => (
        <Box sx={{
          width: { xs: '100%', md: '250px' },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: { xs: 'flex-start', md: 'flex-end' },
          alignItems: 'center',
          gap: '8px',
          paddingRight: '16px',
        }}>
          <Button
            onClick={() => handleApprove(application.id)}
            disabled={actionLoading === application.id}
            primary
            size="regular"
          >
            {actionLoading === application.id ? <CircularProgress size={16} /> : 'Approve'}
          </Button>
          <Button
            onClick={() => handleRequestMoreInfo(application.id)}
            disabled={actionLoading === application.id}
            primary={false}
            bordered
            size="regular"
          >
            Request Info
          </Button>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <CircularProgress sx={{ color: 'var(--palette-light-brand-primary-value-hex)' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', padding: '20px' }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  // Show message if applicationId was provided but not found
  const applicationNotFound = applicationId && filteredApplications.length === 0 && !loading;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {applicationNotFound && (
        <Alert severity="info" sx={{ marginBottom: '16px' }}>
          Application with ID "{applicationId}" not found in pending applications. It may have been approved, declined, or does not exist.
        </Alert>
      )}

        <Table
          columns={columns}
          data={filteredApplications}
          keyExtractor={(item) => item.id}
          emptyMessage={applicationNotFound ? `Application "${applicationId}" not found` : "No pending applications found"}
          loading={loading}
        />
       

      {/* Pagination */}
      {!loading && pagination.totalItems > 0 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          itemLabel="applications"
          showItemCount
        />
      )}

      {/* Request More Info Modal */}
      <RequestMoreInfoModal
        open={requestInfoModalOpen}
        onClose={() => {
          setRequestInfoModalOpen(false);
          setSelectedApplication(null);
        }}
        onSubmit={handleSubmitInfoRequest}
        applicationName={selectedApplication?.businessName || ''}
      />
    </Box>
  );
};

export default ApplicationsNew;

